import React, { FC, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { validateIsraeliId as validateId, validateIsraeliPhone as validatePhone } from 'utils/israeliContact'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Button, Radio, Checkbox, IconButton,  Select, InputLabel, MenuItem } from '@material-ui/core'
import { Edit, Delete, Add, Close, ChevronLeft, ChevronRight, Lock, SwapHoriz } from '@material-ui/icons'
import { DateTimePicker, MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers'
import {
  fetchTags,
  fetchTagTypes,
  fetchUser,
  getAssignTags,
  getFamilyRelations,
  getOneHousehold,
  User
} from './usersApi'
import { buildUserFormData } from './buildUserFormData'
import { isGlobalFieldsLocked } from './globalFields'
import { mergeTypedOverExisting } from './mergeTypedOverExisting'
import { saveFullUser, SaveScratch } from './saveUserAction'
import api from 'service/api'
import { validEmail } from 'utils/validations'
import DateFnsUtils from '@date-io/date-fns'
import DateInputWithCalendar from './CalendarInput' 
import EmergencySection from './Sections/EmergencySection'
import FamilySection from './Sections/FamilySection'

import { TextField } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore, { snackBar } from 'store'

import Users from 'assets/icons/Users.svg'
import Close1Icon from 'assets/icons/ims/Close1.svg'
import SaveIcom from 'assets/icons/ims/Save.svg'
import ContactInfoSection from './Sections/ContactInfoSection'
import PopulationSection from './Sections/PopulationSection'
import DuplicateUserModal from './DuplicateUserModal'
import DefaultCommunityModal from './DefaultCommunityModal'
import { userIsSuperAdmin } from 'utils/roles'


// The dialog is full screen, so without a cap every field grew with the monitor -
// a four-field row was handing ~450px to a nine-digit id number on a 1920 screen.
// The header bar still spans the window; its contents are capped with the form so
// the title sits above the first field rather than out at the edge.
const FORM_MAX_WIDTH = '1120px'

const Header = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 23.992px 16px 23.991px;
  flex-direction: column;
  align-items: flex-end;
  border-bottom: 0.909px solid #E2E8F0; 
  background: rgba(241, 245, 249, 0.30);
`
const HContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: ${FORM_MAX_WIDTH};
  margin: 0 auto;
  min-height: 39.986px;
  padding-right: 0;  
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`
const HLeftContainer = styled.div`
  display: flex;
  min-height: 39.986px;
  align-items: center;
  gap: 12px;
`
const HRightContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: #0F172A;
  font-family: Rubik;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
`
// Reads as a status on the record, not an error - nothing here is wrong, the
// contact simply belongs somewhere else. Same amber as the note inside the
// population section that lists which fields it covers.
const OtherCommunityChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #FCD34D;
  background: #FEF3C7;
  color: #92400E;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`
const ChipLock = styled(Lock)`
  && { font-size: 15px; }
`
// Sits with the primary-community chip rather than with save and cancel: it
// changes the record's home community on its own, not as part of the form's save.
const ChangeCommunityButton = styled(Button)`
  && {
    display: flex;
    padding: 4px 12px;
    align-items: center;
    gap: 6px;
    border-radius: 6px;
    border: 1px solid #CBD5E1;
    background: #FFF;
    color: #334155;
    font-family: Rubik;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
  }
  &&:hover {
    background: #F1F5F9;
  }
`
const ImageIcon = styled(Image)`
  width: 16px;
  height: 16px;
  stroke-width: 1.333px;
  stroke: #FFF;
`
const Button1 = styled(Button)`
  && {
    display: flex;
    min-width: 128px;
    padding: 10px 24px;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    border-radius: 6px;
    background: #2563EB;
    color: #FFF;
    font-size: 15px;
    font-weight: 500;
  }
  &&:hover {
    background: #1D4ED8;
  }
  &&.Mui-disabled {
    background: #BFDBFE;
    color: #F8FAFC;
  }
`
const Button2 = styled(Button)`
  && {
    display: flex;
    padding: 7px 16px;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    border-radius: 6px;
    border: 1px solid #CBD5E1;
    background: #F1F5F9;
    color: #334155;
  }
  &&:hover {
    background: #E2E8F0;
  }
`
const StyledIconButton = styled(IconButton)`
  display: flex;
  width: 35.994px;
  height: 35.994px;
  margin-right: 4px;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
`
const StyledClose = styled(Close)`
  color: #000; 
  font-size: 20px;
`
const Body = styled.div`
  display: flex;
  width: 100%;
  height: calc(100vh - 120px);
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
`
const BodyContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: ${FORM_MAX_WIDTH};
  margin: 0 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: 31.989px;
  flex-shrink: 0;
`
const DialogHeader = styled.div`
   max-height:1000px;
  .MuiDialogTitle-root {
   position: fixed;
    display: flex;
    height: 64px;
    background: ${theme.color.dialogBackground};
    align-items: center;
    justify-content: space-between;
    padding: 16px 36px;
  }

  .MuiIconButton-root {
    padding: 0;
  }
`

// A fresh blank form per call, so loading a contact and then clearing the form
// can never hand back an object another render already wrote into.
const createBlankForm = () => ({
  id: 0,
  // A new contact is created in the community being worked in, which becomes
  // their default - so nothing is locked on this form. Typed, rather than left
  // as the literal null, because a super admin can set it from the form.
  defaultCommunityId: null as number | null,
  // A contact being created belongs to nothing yet; the save relates them to the
  // community being worked in, which is also the primary community they get.
  communityIds: [] as number[],
  population: {
    firstName: '',
    lastName: '',
    identityNumber: '',
    city: '',
    street: '',
    houseNumber: '',
    userTypes: [],
    isDead: false,
    birthDate: null,
    isLocalMember: true,
    deathDate: null,
    email: '',
    phone: '',
    roleInCommunity: '',
    gender: '',
    maritalStatus: '',

  },
  emergency: {
    contacts: []
  },
  family: {
    household: null,
    spouse: null,
    children: []
  },
  contacts: {
    roles: [],
    tags: [],

    // Hidden
    groups: [],
    permissions: {},
  },
})

type AddUserModalProps = {
  open: boolean
  onCloseModal: (open: boolean) => void
  disableEdit: boolean
  user: any
  saveScratch: SaveScratch
  // Bumped by the caller when something outside this form created tags.
  tagListsVersion: number
  onSaved?: () => void
}

const AddUserModal = ({
  open,
  onCloseModal,
  disableEdit,
  user,
  saveScratch,
  tagListsVersion,
  onSaved
}: AddUserModalProps) => {
  const { t } = useTranslation('members')
  const { imsUsersStore, user: userStore, orgs: orgStore, members } = useStore()

  const [saving, setSaving] = useState(false)
  const [duplicateUser, setDuplicateUser] = useState<null | { existingUser: any; fullProfile: any }>(null)
  const [defaultCommunityOpen, setDefaultCommunityOpen] = useState(false)
  // The name the picker reported for a primary community just set from here.
  // orgStore.orgs only holds the manager's own communities, so without this the
  // chip would go back to the unnamed wording right after a change.
  const [changedCommunityName, setChangedCommunityName] = useState<string | undefined>(undefined)
  // What the adopted contact already held, so saving replaces those rows rather
  // than assigning a second copy of each. Its scratch carries that contact's id
  // like any other, so it is only ever applied to the contact it came from.
  const [adopted, setAdopted] = useState<null | {
    originalHousehold: any
    scratch: SaveScratch
  }>(null)
  const initialFormRef = useRef<typeof form | null>(null)
  const [error, setValidationError] = useState({ population: true })
  // Shaped like the responses from the start: the sections read `tags.data` and
  // `tagTypes.data` straight out of render, so a bare [] would throw if one of
  // them is still in flight when the dialog paints.
  const [tagTypes, setTagTypes] = useState<{ data: any[] }>({ data: [] })
  const [tags, setTags] = useState<{ data: any[] }>({ data: [] })
  // What the cached tag lists were read for, or undefined when they have to be
  // read again. The community and the version are both in the key: the version
  // is how the import flow says it created tags, since it cannot reach in here.
  const tagListsKey = `${orgStore.currentOrgId}:${tagListsVersion}`
  const tagListsFetchedKey = useRef<string | undefined>(undefined)
  const invalidateTagLists = () => {
    tagListsFetchedKey.current = undefined
  }

  const [form, setForm] = useState(createBlankForm)

  // Which contact the form currently holds, and a counter that moves every time
  // a different one is loaded.
  const [loaded, setLoaded] = useState<{ user: any, instance: number }>({ user: null, instance: 0 })

  // Swapped in during render rather than from an effect. An effect runs after
  // the paint, so the dialog opened once showing the previously edited contact
  // and the sections seeded their own state from it - the spouse block's
  // open/closed state is `useState(value.spouse)`, read at mount only - and that
  // seeding survived the correction that followed. Assigning state during
  // render instead makes React re-render before committing, so this contact's
  // values are the first ones anything sees.
  if (loaded.user !== user) {
    setLoaded({ user, instance: loaded.instance + 1 })
    setForm(user || createBlankForm())
    initialFormRef.current = user || null
    setValidationError({ population: true })
    // Scratch state belonging to the contact we were just on. Left behind,
    // `adopted` made every later save replay that contact's household, family
    // and tag rows.
    setAdopted(null)
    setDuplicateUser(null)
    setDefaultCommunityOpen(false)
    setChangedCommunityName(undefined)
  }

  // The tag lists are only read inside the dialog, but this component is mounted
  // for as long as the population tab is open — so fetching on mount charged two
  // requests to every manager who never opened the form. Fetch on first open
  // instead, and only again when the community changes.
  useEffect(() => {
    if (!open || tagListsFetchedKey.current === tagListsKey) {
      return
    }
    tagListsFetchedKey.current = tagListsKey

    fetchTags(orgStore.currentOrgId)
      .then(items => {
        setTags(items)
      })
      // Clear the marker on failure so the next open can try again.
      .catch(invalidateTagLists)

    fetchTagTypes()
      .then(items => {
        setTagTypes(items)
      })
      .catch(invalidateTagLists)
  }, [open, tagListsKey])

  const setError = (patch: any) => {
    setValidationError({ ...error, ...patch })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await saveFullUser(form, orgStore.currentOrgId, {
        originalHousehold: adopted ? adopted.originalHousehold : user?.family?.household,
        scratch: adopted ? adopted.scratch : saveScratch,
      })    

      if (!res) {
        return
      }

      if (res?.data?.status === 409 && res?.data?.data?.code === 'DUPLICATE_USER') {
        const communityIds: number[] = res.data?.data?.fullProfile?.communityIds ?? []
        
        if (!communityIds.includes(orgStore.currentOrgId)) {
          setDuplicateUser({ existingUser: res.data?.data?.existingUser, fullProfile: res.data?.data?.fullProfile })
          return
        }
        
        snackBar.showError(t`population:duplicate_in_community`)
        return
      }

      snackBar.showSuccess(t`population:contact_saved`)
      // A save can create tags - anything the form marked action: "new" - so the
      // cached lists no longer describe the community. Dropping the marker makes
      // the next open re-read them, which is what a page refresh used to be
      // needed for before a newly added tag showed up on another contact.
      invalidateTagLists()
      onCloseModal(false)
      onSaved?.()
    } catch (e: any) {
      snackBar.showError(e?.message || t`population:contact_save_error`)
    } finally {
      setSaving(false)
    }
  }

  const handleUseExisting = async () => {
    if (!duplicateUser) return
    const existingUserId = duplicateUser.existingUser?.existingUserId
    if (!existingUserId) return

    setSaving(true)
    try {
      // The contact has to belong to this community before their record can be
      // read against it, or saved back to it.
      const res = await api.relateUserToCommunity(orgStore.currentOrgId, existingUserId)

      if (!res) return

      const [userRes, family, tags, household] = await Promise.all([
        fetchUser(existingUserId, orgStore.currentOrgId),
        getFamilyRelations(existingUserId, orgStore.currentOrgId),
        getAssignTags(existingUserId, orgStore.currentOrgId),
        getOneHousehold(existingUserId, orgStore.currentOrgId),
      ])

      const existingForm = buildUserFormData(userRes?.data, family, tags, household?.houseHold)
      const existingDefaultCommunityId = existingForm.defaultCommunityId
      setForm(prev => mergeTypedOverExisting(
        existingForm,
        prev,
        existingUserId,
        isGlobalFieldsLocked(existingDefaultCommunityId, orgStore.currentOrgId),
      ))
      setAdopted({
        originalHousehold: household?.houseHold,
        scratch: {
          contactId: existingUserId,
          familyRelationIds: (family ?? []).map((relation: any) => relation.id).filter(Boolean),
          tagRelations: tags ?? [],
        },
      })

      // The form stays open on the merged contact: the manager sees what is
      // about to be saved and presses save themselves.
      setDuplicateUser(null)
      snackBar.showSuccess(t`population:existing_contact_loaded`)
      await members.getMembers(orgStore.currentOrgId)
    } catch (e: any) {
      console.error(e)
      snackBar.showError(e?.message || t`population:contact_save_error`)
    } finally {
      setSaving(false)
    }
  }

  const updateSection = (section: keyof typeof form, value: any) => {
    setForm(prev => ({ ...prev, [section]: value,}))
  }

  const globalFieldsLocked = isGlobalFieldsLocked(form.defaultCommunityId, orgStore.currentOrgId)

  // Only resolvable when the manager belongs to that community as well, which is
  // not a given - orgStore.orgs holds their own communities. The picker's own
  // answer is preferred since it read the full list. Unnamed, the label just
  // says the primary community is elsewhere.
  const defaultCommunityName = globalFieldsLocked
    ? changedCommunityName ?? orgStore.orgs?.[String(form.defaultCommunityId)]?.name
    : undefined

  // Only a super admin may move a contact to another primary community, and only
  // on a contact that exists - a new one gets the community being worked in.
  const canChangeDefaultCommunity = !disableEdit && !!form.id && userIsSuperAdmin()

  // Written straight into the loaded-form baseline as well as the form: the save
  // button compares the two to tell whether anything is pending, and this change
  // was already written on its own.
  const handleDefaultCommunityChanged = (newDefaultCommunityId: number, name?: string) => {
    setForm(prev => ({ ...prev, defaultCommunityId: newDefaultCommunityId }))
    if (initialFormRef.current) {
      initialFormRef.current = { ...initialFormRef.current, defaultCommunityId: newDefaultCommunityId }
    }
    setChangedCommunityName(name)
  }

  // The form is one component for all three ways in, and it called itself "add a
  // new community member" in every one of them.
  const title = !form.id
    ? t`population:add_new_member`
    : disableEdit
      ? t`population:view_member`
      : t`population:edit_member`

  return (
    <>
      <Dialog 
        style={{ zIndex: 1200 }} 
        open={open} 
        fullWidth 
        fullScreen
        maxWidth={'lg'}
        scroll={'body'}
      >
        <DialogHeader>
          <Header>
            <HContainer>                    
              <HRightContainer>
                {title}
                {globalFieldsLocked && (
                  <OtherCommunityChip>
                    <ChipLock />
                    {defaultCommunityName
                      ? t('population:not_default_community_named', { community: defaultCommunityName })
                      : t`population:not_default_community`}
                  </OtherCommunityChip>
                )}
                {canChangeDefaultCommunity && (
                  <ChangeCommunityButton
                    onClick={() => setDefaultCommunityOpen(true)}
                    startIcon={<SwapHoriz />}
                  >{t`population:change_default_community`}</ChangeCommunityButton>
                )}
              </HRightContainer>
              <HLeftContainer>
                {!disableEdit && 
                <>
                  <Button1
                    onClick={handleSave}
                    disabled={
                      saving ||
                      JSON.stringify(form) === JSON.stringify(initialFormRef.current) ||
                      // Name and id number are only the manager's to answer for when
                      // they can edit them. Locked, they are whatever the contact's
                      // home community holds - and a legacy record with a blank name
                      // or an id number that fails the check digit would otherwise
                      // block saving the fields this community *can* change.
                      (!globalFieldsLocked && (
                        !form.population.firstName?.trim() ||
                        !form.population.lastName?.trim() ||
                        !validateId(form.population.identityNumber?.trim() || '')
                      )) ||
                      (!validEmail(form.population.email) && !validatePhone(form.population.phone))
                    }
                    startIcon={saving ? <Spinner size={16} /> : <ImageIcon src={SaveIcom} />} >{t`population:save`}
                  </Button1>
                  <Button2 onClick={() => onCloseModal(false)} startIcon={<ImageIcon src={Close1Icon}/>}>{t`population:cancel`}</Button2>
                </>}
                <StyledIconButton onClick={() => onCloseModal(false)}><StyledClose/></StyledIconButton>
              </HLeftContainer>
            </HContainer>
          </Header>
          </DialogHeader>
          <DialogContent>
            <Body>
                {/* Keyed per loaded contact: the sections derive local state
                    from `value` at mount, so they have to be rebuilt for each
                    one rather than reused. */}
                <BodyContainer key={loaded.instance}>
                    <PopulationSection  
                      value={form.population}
                      onChange={v => updateSection('population', v)}
                      disableEdit={disableEdit}
                      globalFieldsLocked={globalFieldsLocked}
                      setError={setError}
                      tags={tags}
                      tagTypes={tagTypes.data}
                    />
                    <EmergencySection
                      value={form.emergency}
                      onChange={(v: any) => updateSection('emergency', v)}
                      disableEdit={disableEdit}
                    />            
                    <FamilySection
                      value={form.family}
                      onChange={(v: any) => updateSection('family', v)}
                      houseNumber={form.population.houseNumber}
                      disableEdit={disableEdit}
                      currentUserId={form.id}
                     />
                    <ContactInfoSection
                      value={form.contacts}
                      onChange={v => updateSection('contacts', v)}
                      disableEdit={disableEdit}
                      tags={tags}
                      tagTypes={tagTypes.data}
                    />
                </BodyContainer>
            </Body>
            </DialogContent>
        </Dialog>

      {canChangeDefaultCommunity && (
        <DefaultCommunityModal
          open={defaultCommunityOpen}
          contactId={form.id}
          contactName={[form.population.firstName, form.population.lastName].filter(Boolean).join(' ')}
          currentDefaultCommunityId={form.defaultCommunityId}
          memberCommunityIds={form.communityIds}
          onClose={() => setDefaultCommunityOpen(false)}
          onChanged={handleDefaultCommunityChanged}
        />
      )}

      {duplicateUser && (
        <DuplicateUserModal
          open={true}
          existingUser={duplicateUser.existingUser}
          saving={saving}
          onBack={() => setDuplicateUser(null)}
          onUseExisting={handleUseExisting}
          formPhone={form.population.phone}
          formEmail={form.population.email}
          formIsraeliId={form.population.identityNumber}
        />
      )}
    </>
  )
}

export default observer(AddUserModal)


